// schwalbennest-ruegen.com: Anfragen (Liste, Status, Schnellantwort), Live-Prüfung, Preise, Google-Freigabe
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Globe, RefreshCw, Loader2, Send, CheckCircle2, RotateCcw, Mail, Phone, ExternalLink, Copy, Search, Tag, AlarmClock, Trash2, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./components/ui/dialog";
import { Switch } from "./components/ui/switch";
import { WatchChecks } from "./Gerd40Panel";
import { VisitorStats } from "./ComStats";
import { GoogleIndex } from "./ComGoogle";
import { RedirectCard } from "./ComRedirect";
import { EasybookingCard } from "./SitePanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/com`;
const err = (e, f) => e.response?.data?.detail || f;
const fmt = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Berlin" }) : "–");
const btn = "flex items-center gap-2 text-xs px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5 transition-colors disabled:opacity-50";
const Card = ({ children, testid }) => <div className="bg-eb-surface border border-white/5 rounded-xl p-5" data-testid={testid}>{children}</div>;
const FORM_COLORS = { kontakt: "#38BDF8", camping: "#10B981", eventraum: "#F59E0B", gruppenreisen: "#A78BFA", gutschein: "#F472B6", fruehling: "#84CC16", gerd_wird_40: "#FB923C", herbstfest: "#D97706" };
const CHANGED = "com-inquiries-changed";

// Zahl offener .com-Anfragen fürs Cockpit-Menü (minütlich + sofort nach Änderungen im Panel)
export function useComOpenCount(enabled) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    const load = () => axios.get(`${API}/inquiries/count`).then((r) => setN(r.data.offen)).catch(() => {});
    load();
    const id = setInterval(load, 60000);
    window.addEventListener(CHANGED, load);
    return () => { clearInterval(id); window.removeEventListener(CHANGED, load); };
  }, [enabled]);
  return n;
}

function Stat({ label, value, ok, testid }) {
  return (
    <div className="bg-black/25 rounded-lg p-3" data-testid={testid}>
      <div className={`text-sm font-semibold ${ok === undefined ? "" : ok ? "text-emerald-300" : "text-amber-300"}`}>{value}</div>
      <div className="text-[11px] text-eb-muted uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
}

function MorningLine({ site, busy, run }) {
  const m = site.morning;
  const mail = m?.mail?.sent ? " · E-Mail verschickt" : "";
  return (
    <div className="flex items-center gap-3 mt-3 flex-wrap" data-testid="com-morning">
      <button className={btn} disabled={!!busy} data-testid="com-morning-run" onClick={() => {
        if (!window.confirm(`Morgen-Check jetzt starten? Bei Problemen geht sofort eine E-Mail an ${site.alert_to}.`)) return;
        run("morning", () => axios.post(`${API}/morning-check`));
      }}>
        {busy === "morning" ? <Loader2 size={13} className="animate-spin" /> : <AlarmClock size={13} />} Morgen-Check jetzt
      </button>
      <span className="text-[11px] text-eb-muted">Täglich 06:00 Uhr automatisch · bei Problemen E-Mail an {site.alert_to}</span>
      {m && <span className={`text-[11px] font-semibold ${m.ok ? "text-emerald-300" : "text-amber-300"}`} data-testid="com-morning-last">
        Zuletzt {fmt(m.at)}: {m.ok ? "alles in Ordnung" : `${m.problems.length} Problem(e)`}{mail}</span>}
    </div>
  );
}

function SiteCard({ site, reload }) {
  const [busy, setBusy] = useState("");
  const [msg, setMsg] = useState("");
  const run = async (key, fn) => {
    setBusy(key); setMsg("");
    try { await fn(); await reload(); } catch (e) { setMsg(err(e, "Aktion fehlgeschlagen")); }
    setBusy("");
  };
  const live = site.live;
  const pr = site.prices || {};
  return (
    <Card testid="com-site">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-heading font-semibold flex items-center gap-2"><Globe size={16} /> schwalbennest-ruegen.com</h3>
          <p className="text-xs text-eb-muted mt-1">WordPress bei IONOS mit dem Schwalbennest-Theme · Inhalte, Design und Preise kommen automatisch von hier.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <a href={site.url} target="_blank" rel="noreferrer" className={btn} data-testid="com-open-site"><ExternalLink size={13} /> Website öffnen</a>
          <a href="/com/" target="_blank" rel="noreferrer" className={btn} data-testid="com-open-guide"><ExternalLink size={13} /> Theme &amp; Anleitung</a>
          <button className={btn} disabled={!!busy} onClick={() => run("live", () => axios.post(`${API}/live-check`))} data-testid="com-live-check">
            {busy === "live" ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />} Live prüfen
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
        <Stat label="Theme verbunden" value={site.connected ? `ja · ${fmt(site.last_fetch_at)}` : site.last_fetch_at ? `zuletzt ${fmt(site.last_fetch_at)}` : "noch nicht"} ok={site.connected} testid="com-stat-connected" />
        <Stat label="Inhaltsstand" value={site.last_fetch_cv && site.last_fetch_cv !== site.cv ? `${site.cv} (Seite: ${site.last_fetch_cv})` : site.cv} testid="com-stat-cv" />
        <Stat label="Google" value={site.index ? "freigegeben" : "gesperrt (noindex)"} ok={site.index} testid="com-stat-google" />
        <Stat label="Preise von der .de" value={pr.fetched_at ? `${Object.values(pr.counts || {}).reduce((a, b) => a + b, 0)} Tabellen · ${fmt(pr.fetched_at)}` : "noch nicht geholt"}
          ok={pr.fetched_at ? !(pr.errors || []).length : undefined} testid="com-stat-prices" />
      </div>
      <div className="flex items-center gap-3 mt-3 flex-wrap">
        <button className={btn} disabled={!!busy} onClick={() => run("prices", () => axios.post(`${API}/prices/refresh`))} data-testid="com-prices-refresh">
          {busy === "prices" ? <Loader2 size={13} className="animate-spin" /> : <Tag size={13} />} Preise jetzt von der .de holen
        </button>
        <span className="text-[11px] text-eb-muted">Automatisch alle 6 Std.; die .com übernimmt Änderungen binnen 15 Min.</span>
        {(pr.errors || []).length > 0 && <span className="text-[11px] text-amber-300" data-testid="com-prices-errors">{pr.errors.join(" · ")}</span>}
      </div>
      <MorningLine site={site} busy={busy} run={run} />
      {msg && <p className="text-sm text-rose-400 mt-3" data-testid="com-site-error">{msg}</p>}
      {live && (
        <div className="mt-4" data-testid="com-live-result">
          <div className={`text-sm font-semibold flex items-center gap-2 ${live.ok ? "text-emerald-400" : "text-rose-400"}`} data-testid="com-live-status">
            {live.ok ? "Live-Prüfung: alles in Ordnung" : "Live-Prüfung: es gibt Probleme"} <span className="text-eb-muted font-normal text-xs">· {fmt(live.at)}</span>
          </div>
          <WatchChecks checks={live.checks} prefix="com-live" />
        </div>
      )}
    </Card>
  );
}

function GoogleCard({ site, reload }) {
  const [gsv, setGsv] = useState(site.gsv || "");
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setGsv(site.gsv || ""); }, [site.gsv]);
  const save = async (patch) => {
    setBusy(true); setMsg("");
    try {
      const r = await axios.put(`${API}/site`, patch);
      setGsv(r.data.gsv || "");
      await reload();
      setMsg("Gespeichert – die .com übernimmt es binnen 15 Minuten.");
    }
    catch (e) { setMsg(err(e, "Speichern fehlgeschlagen")); }
    setBusy(false);
  };
  const copy = () => navigator.clipboard?.writeText(site.sitemap);
  return (
    <Card testid="com-google">
      <h3 className="font-heading font-semibold">Google-Freigabe &amp; Search Console</h3>
      <div className="flex items-center gap-3 mt-4">
        <Switch checked={site.index} disabled={busy} className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-white/20 border-white/10 [&>span]:bg-white"
          onCheckedChange={(v) => {
          if (v && !window.confirm("schwalbennest-ruegen.com jetzt für Google freigeben?")) return;
          save({ index: v });
        }} data-testid="com-google-switch" />
        <span className="text-sm">{site.index ? "Google darf die Seite finden" : "Google ist ausgesperrt (noindex)"}</span>
      </div>
      <div className="mt-4 grid md:grid-cols-[1fr_auto] gap-2">
        <input value={gsv} onChange={(e) => setGsv(e.target.value)} placeholder='Search-Console-Code oder ganzes <meta name="google-site-verification" …>'
          className="bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="com-gsv-input" />
        <button className={btn} disabled={busy} onClick={() => save({ gsv })} data-testid="com-gsv-save">Code speichern</button>
      </div>
      <div className="mt-3 flex items-center gap-2 text-xs text-eb-muted flex-wrap">
        Sitemap für die Search Console: <code className="text-eb-fg" data-testid="com-sitemap-url">{site.sitemap}</code>
        <button className={btn} onClick={copy} data-testid="com-sitemap-copy"><Copy size={12} /> Kopieren</button>
      </div>
      {msg && <p className="text-xs mt-3 text-eb-muted" data-testid="com-google-msg">{msg}</p>}
      <GoogleIndex />
    </Card>
  );
}

function ReplyDialog({ inq, onClose, onDone }) {
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    if (!inq) return;
    setDraft(null); setMsg("");
    axios.get(`${API}/inquiries/${inq.id}/template`).then((r) => setDraft(r.data)).catch((e) => setMsg(err(e, "Vorlage nicht ladbar")));
  }, [inq]);
  const send = async () => {
    setBusy(true); setMsg("");
    try { onDone((await axios.post(`${API}/inquiries/${inq.id}/reply`, draft)).data); }
    catch (e) { setMsg(err(e, "Senden fehlgeschlagen")); }
    setBusy(false);
  };
  return (
    <Dialog open={!!inq} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-eb-surface border-white/10 text-eb-fg max-w-2xl" data-testid="com-reply-dialog">
        <DialogHeader><DialogTitle>Schnellantwort an {inq?.name}</DialogTitle></DialogHeader>
        <p className="text-xs text-eb-muted">An {inq?.email} · Absender fh@schwalbennest-ruegen.com · Antworten des Gastes gehen an info@schwalbennest-ruegen.de</p>
        {!draft ? <Loader2 className="animate-spin" size={18} /> : (
          <div className="space-y-3">
            <input value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm" data-testid="com-reply-subject" />
            <textarea value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })} rows={12}
              className="w-full bg-black/30 border border-white/10 rounded-md px-3 py-2 text-sm leading-relaxed" data-testid="com-reply-text" />
            <p className="text-[11px] text-eb-muted">Texte in [eckigen Klammern] ersetzen. Adresse, Telefon und die Anfrage des Gastes werden automatisch unten angehängt.</p>
          </div>
        )}
        {msg && <p className="text-sm text-rose-400" data-testid="com-reply-error">{msg}</p>}
        <div className="flex justify-end gap-2">
          <button className={btn} onClick={onClose} data-testid="com-reply-cancel">Abbrechen</button>
          <button onClick={send} disabled={busy || !draft} data-testid="com-reply-send"
            className="flex items-center gap-2 text-sm px-4 py-2 rounded-md bg-eb-primary text-black font-semibold hover:opacity-90 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Antwort senden
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const confirmText = (m) => (m.sent ? "gesendet" : m.skipped ? "übersprungen (Testadresse)" : m.queued ? "wird gesendet" : `fehlgeschlagen${m.error ? ` – ${m.error}` : ""}`);

function InquiryRow({ r, open, toggle, onReply, onStatus, onDelete }) {
  const done = r.status === "beantwortet";
  return (
    <div className="border-t border-white/5" data-testid={`com-inq-${r.id}`}>
      <button onClick={toggle} className="w-full text-left py-3 grid md:grid-cols-[110px_120px_1fr_120px] gap-2 items-center hover:bg-white/[0.02] px-1" data-testid={`com-inq-open-${r.id}`}>
        <span className="text-xs text-eb-muted">{fmt(r.created_at)}</span>
        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full w-fit" style={{ background: `${FORM_COLORS[r.form]}22`, color: FORM_COLORS[r.form] }}>{r.form_title}</span>
        <span className="text-sm min-w-0"><b>{r.name}</b> <span className="text-eb-muted">· {r.summary}</span>
          {r.voucher && <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-lime-500/15 text-lime-300" data-testid={`com-inq-voucher-${r.id}`}>Code {r.voucher}</span>}
          {r.form === "gutschein" && r.amount_eur != null && (
            <span className={`ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded ${r.paid ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`} data-testid={`com-inq-paid-${r.id}`}>
              {r.paid ? `bezahlt · ${r.amount_eur.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €` : `Zahlung offen · ${r.amount_eur.toLocaleString("de-DE", { minimumFractionDigits: 2 })} €`}
            </span>
          )}
          {r.voucher_codes?.length > 0 && (
            <span className="ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-300" data-testid={`com-inq-vouchercode-${r.id}`}>
              {r.voucher_codes.join(" · ")}
            </span>
          )}</span>
        <span className={`text-[11px] font-semibold w-fit px-2 py-0.5 rounded-full ${done ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`} data-testid={`com-inq-status-${r.id}`}>
          {done ? "beantwortet" : "offen"}</span>
      </button>
      {open && (
        <div className="pb-4 px-1" data-testid={`com-inq-detail-${r.id}`}>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1 text-sm bg-black/20 rounded-lg p-3">
            {r.fields.map((f) => <div key={f.key} className="min-w-0"><span className="text-eb-muted text-xs">{f.label}: </span><span className="break-words whitespace-pre-wrap">{f.value}</span></div>)}
          </div>
          {r.replies.length > 0 && <p className="text-[11px] text-emerald-300 mt-2" data-testid={`com-inq-replied-${r.id}`}>
            Beantwortet {fmt(r.answered_at)} von {r.answered_by} · „{r.replies[r.replies.length - 1].subject}“</p>}
          {done && !r.replies.length && <p className="text-[11px] text-emerald-300 mt-2">Als beantwortet markiert {fmt(r.answered_at)} von {r.answered_by}</p>}
          {r.confirm_mail && <p className="text-[11px] text-eb-muted mt-2" data-testid={`com-inq-confirm-${r.id}`}>
            Eingangsbestätigung an den Gast: {confirmText(r.confirm_mail)}{!done && r.reminder_count > 0 ? ` · ${r.reminder_count}× erinnert` : ""}</p>}
          <div className="flex gap-2 mt-3 flex-wrap">
            <button onClick={() => onReply(r)} data-testid={`com-inq-reply-${r.id}`}
              className="flex items-center gap-2 text-xs px-3 py-1.5 rounded-md bg-eb-primary text-black font-semibold hover:opacity-90"><Send size={13} /> Schnellantwort</button>
            <button className={btn} onClick={() => onStatus(r, done ? "offen" : "beantwortet")} data-testid={`com-inq-toggle-${r.id}`}>
              {done ? <><RotateCcw size={13} /> Wieder öffnen</> : <><CheckCircle2 size={13} /> Als beantwortet markieren</>}</button>
            <a className={btn} href={`mailto:${r.email}`} data-testid={`com-inq-mail-${r.id}`}><Mail size={13} /> {r.email}</a>
            {r.telefon && <a className={btn} href={`tel:${r.telefon.replace(/[^\d+]/g, "")}`} data-testid={`com-inq-tel-${r.id}`}><Phone size={13} /> {r.telefon}</a>}
            {r.voucher_codes?.length > 0 && (
              <button className={btn} onClick={() => window.open(`${API}/inquiries/${r.id}/voucher.pdf`, "_blank")} data-testid={`com-inq-voucher-pdf-${r.id}`}>
                <FileText size={13} /> Gutschein-PDF
              </button>
            )}
            <button className={`${btn} ml-auto text-rose-300 border-rose-400/30`} onClick={() => onDelete(r)} data-testid={`com-inq-delete-${r.id}`}><Trash2 size={13} /> Löschen</button>
          </div>
        </div>
      )}
    </div>
  );
}

function WeeklyReport() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => axios.get(`${API}/weekly-report`).then((r) => setD(r.data)).catch((e) => setMsg(err(e, "Wochenbericht nicht ladbar"))), []);
  useEffect(() => {
    load();
    window.addEventListener(CHANGED, load);
    return () => window.removeEventListener(CHANGED, load);
  }, [load]);
  const send = async () => {
    if (!window.confirm(`Wochenbericht jetzt an ${d.to.join(" und ")} senden?`)) return;
    setBusy(true); setMsg("");
    try {
      const r = await axios.post(`${API}/weekly-report/send`);
      setMsg(r.data.mail.every((m) => m.sent) ? "Wochenbericht verschickt." : `Versand fehlgeschlagen: ${r.data.mail.filter((m) => !m.sent).map((m) => m.to).join(", ")}`);
      load();
    } catch (e) { setMsg(err(e, "Versand fehlgeschlagen")); }
    setBusy(false);
  };
  if (!d) return msg ? <p className="text-sm text-rose-400 mt-3">{msg}</p> : null;
  const s = d.stats;
  const last = d.last;
  return (
    <div className="mt-4 pt-4 border-t border-white/5" data-testid="com-weekly">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-sm"><b>Wochenbericht</b> <span className="text-eb-muted text-xs">· {d.schedule} an {d.to.join(" + ")}</span></div>
        <div className="flex gap-2">
          <a className={btn} href={`${API}/weekly-report/preview`} target="_blank" rel="noreferrer" data-testid="com-weekly-preview"><ExternalLink size={13} /> Vorschau</a>
          <button className={btn} disabled={busy} onClick={send} data-testid="com-weekly-send">
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Jetzt senden</button>
        </div>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
        <Stat label={`Neu · ${s.label}`} value={s.new} testid="com-weekly-new" />
        <Stat label="Beantwortet (7 Tage)" value={s.answered} testid="com-weekly-answered" />
        <Stat label="Noch offen" value={s.open} ok={s.open === 0} testid="com-weekly-open" />
        <Stat label="Ø Antwortzeit" value={s.avg || "–"} testid="com-weekly-avg" />
      </div>
      {last && <p className="text-[11px] text-eb-muted mt-2" data-testid="com-weekly-last">
        Zuletzt {last.source === "cron" ? "automatisch" : "von Hand"} {fmt(last.at)} · {last.mail.length ? `${last.mail.filter((m) => m.sent).length}/${last.mail.length} E-Mails zugestellt` : "ohne E-Mail (Vorschau-Umgebung)"}</p>}
      {msg && <p className="text-xs text-eb-muted mt-2" data-testid="com-weekly-msg">{msg}</p>}
    </div>
  );
}

function Inquiries() {
  const [tab, setTab] = useState("offen");
  const [data, setData] = useState(null);
  const [open, setOpen] = useState("");
  const [reply, setReply] = useState(null);
  const [msg, setMsg] = useState("");
  const load = useCallback(async () => {
    try { setData((await axios.get(`${API}/inquiries`, { params: { status: tab } })).data); setMsg(""); window.dispatchEvent(new Event(CHANGED)); }
    catch (e) { setMsg(err(e, "Anfragen nicht ladbar")); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setOpen(""); }, [tab]);
  const status = async (r, s) => {
    try { await axios.post(`${API}/inquiries/${r.id}/status`, { status: s }); load(); }
    catch (e) { setMsg(err(e, "Status nicht änderbar")); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Anfrage von ${r.name} endgültig löschen (z. B. Spam oder Test)?`)) return;
    try { await axios.delete(`${API}/inquiries/${r.id}`); setOpen(""); load(); }
    catch (e) { setMsg(err(e, "Löschen nicht möglich")); }
  };
  return (
    <Card testid="com-inquiries">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h3 className="font-heading font-semibold">Anfragen von der .com</h3>
        <div className="flex gap-2 items-center">
          {["offen", "beantwortet", "alle"].map((k) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`com-tab-${k}`}
              className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${tab === k ? "border-eb-primary text-eb-primary bg-white/[0.04]" : "border-white/10 text-eb-muted hover:text-eb-fg"}`}>
              {k[0].toUpperCase() + k.slice(1)} <span data-testid={`com-count-${k}`}>{data?.counts?.[k] ?? "–"}</span></button>
          ))}
          <button className={btn} onClick={load} data-testid="com-inq-reload"><RefreshCw size={13} /></button>
        </div>
      </div>
      {msg && <p className="text-sm text-rose-400 mt-3">{msg}</p>}
      {!data ? <Loader2 className="animate-spin mt-4" size={18} /> : data.items.length === 0 ? (
        <p className="text-sm text-eb-muted mt-4" data-testid="com-inq-empty">{tab === "offen" ? "Keine offenen Anfragen." : "Noch keine Anfragen."}</p>
      ) : (
        <div className="mt-3" data-testid="com-inq-list">
          {data.items.map((r) => <InquiryRow key={r.id} r={r} open={open === r.id} toggle={() => setOpen(open === r.id ? "" : r.id)} onReply={setReply} onStatus={status} onDelete={remove} />)}
        </div>
      )}
      <p className="text-[11px] text-eb-muted mt-3" data-testid="com-inq-footer">Jede neue Anfrage geht per E-Mail an info@schwalbennest-ruegen.de, der Gast bekommt sofort eine Eingangsbestätigung (Antwort binnen 24 Std. zugesagt). Ist eine Anfrage länger als 24 Std. offen, kommt täglich (7–21 Uhr) eine Erinnerung an info@ und fh@.</p>
      <WeeklyReport />
      <ReplyDialog inq={reply} onClose={() => setReply(null)} onDone={() => { setReply(null); load(); }} />
    </Card>
  );
}

export function ComPanel() {
  const [site, setSite] = useState(null);
  const [msg, setMsg] = useState("");
  const reload = useCallback(async () => {
    try { setSite((await axios.get(`${API}/site`)).data); } catch (e) { setMsg(err(e, "Status nicht ladbar")); }
  }, []);
  useEffect(() => { reload(); }, [reload]);
  return (
    <div className="space-y-5" data-testid="com-panel">
      <div>
        <h1 className="font-heading text-2xl font-bold">Website schwalbennest-ruegen.com</h1>
        <p className="text-sm text-eb-muted mt-1">Anfragen beantworten, Besucher sehen, Live-Stand prüfen, Preise abgleichen und Google freigeben.</p>
      </div>
      {msg && <p className="text-sm text-rose-400">{msg}</p>}
      <Inquiries />
      <EasybookingCard slug="com" api={API} />
      <VisitorStats />
      {site ? <SiteCard site={site} reload={reload} /> : <Card><Loader2 className="animate-spin" size={18} /></Card>}
      {site && <GoogleCard site={site} reload={reload} />}
      <RedirectCard />
    </div>
  );
}
